import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import { useTranslation } from 'react-i18next';

interface DuplicateDaysModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (selectedDays: number[]) => void;
  sourceDayIndex: number;
}

const DuplicateDaysModal: React.FC<DuplicateDaysModalProps> = ({
  isOpen,
  onClose,
  onApply,
  sourceDayIndex
}) => {
  const { t } = useTranslation();
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  
  // Les jours de la semaine (0 = Dimanche, 1 = Lundi, etc.)
  const daysOfWeek = [
    { index: 0, name: 'Dimanche' },
    { index: 1, name: 'Lundi' },
    { index: 2, name: 'Mardi' },
    { index: 3, name: 'Mercredi' },
    { index: 4, name: 'Jeudi' },
    { index: 5, name: 'Vendredi' },
    { index: 6, name: 'Samedi' }
  ];
  
  // Filtrer pour ne pas afficher le jour source dans les options
  const availableDays = daysOfWeek.filter(day => day.index !== sourceDayIndex);
  
  const handleCheckboxChange = (dayIndex: number) => {
    setSelectedDays(prev => {
      if (prev.includes(dayIndex)) {
        return prev.filter(day => day !== dayIndex);
      } else {
        return [...prev, dayIndex];
      }
    });
  };
  
  const handleApply = () => {
    onApply(selectedDays);
    onClose();
    setSelectedDays([]); // Réinitialiser la sélection
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-sm w-full max-h-[90vh] overflow-auto border border-gray-100 dark:border-gray-800">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              COPIER LES HORAIRES DANS...
            </h3>
            <button 
              type="button"
              onClick={onClose}
              className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <X size={20} className="text-gray-500 dark:text-gray-400" />
            </button>
          </div>
          
          <div className="mb-6">
            {availableDays.map((day) => (
              <div key={day.index} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-800">
                <span className="text-gray-800 dark:text-gray-200">{day.name}</span>
                <label className="relative flex items-center">
                  <input
                    type="checkbox"
                    className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 rounded"
                    checked={selectedDays.includes(day.index)}
                    onChange={() => handleCheckboxChange(day.index)}
                  />
                </label>
              </div>
            ))}
          </div>
          
          <div className="flex justify-center">
            <Button
              onClick={handleApply}
              className="w-full"
              disabled={selectedDays.length === 0}
            >
              Appliquer
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DuplicateDaysModal;
